import { pointInRect } from './geometry';
import type { ActorFootprint, Point } from './geometry';
import { footprintCollidesWithRegion } from './collision';
import type { RegionDefinition, RegionEntrance } from './region-definition';

export interface MovementResolution {
  position: Point;
  blockedX: boolean;
  blockedY: boolean;
  crossedEntrance: RegionEntrance | null;
}

function clamp(value: number, minimum: number, maximum: number): number {
  if (minimum > maximum) return (minimum + maximum) / 2;
  return Math.max(minimum, Math.min(value, maximum));
}

function entranceAt(region: RegionDefinition, position: Point): RegionEntrance | null {
  return region.entrances.find((entrance) => pointInRect(position, entrance.area)) ?? null;
}

function pathIsClear(
  region: RegionDefinition,
  from: Point,
  target: Point,
  footprint: ActorFootprint,
): boolean {
  const distance = Math.hypot(target.x - from.x, target.y - from.y);
  const stepLength = Math.max(4, Math.min(footprint.radiusX, footprint.radiusY) / 2);
  const steps = Math.max(1, Math.ceil(distance / stepLength));

  for (let step = 1; step <= steps; step += 1) {
    const progress = step / steps;
    const point = {
      x: from.x + (target.x - from.x) * progress,
      y: from.y + (target.y - from.y) * progress,
    };
    if (footprintCollidesWithRegion(region, point, footprint)) return false;
  }
  return true;
}

/** Throws a stable RangeError when the movement origin is not safe for the normalized radius. */
export function resolveRegionMovement(input: {
  region: RegionDefinition;
  from: Point;
  desired: Point;
  radius: number;
  footprint?: ActorFootprint;
}): MovementResolution {
  const { region, from, desired } = input;
  const radius = Number.isFinite(input.radius) && input.radius > 0 ? input.radius : 0;
  const footprint = input.footprint
    ? {
        radiusX: Number.isFinite(input.footprint.radiusX) && input.footprint.radiusX > 0 ? input.footprint.radiusX : radius,
        radiusY: Number.isFinite(input.footprint.radiusY) && input.footprint.radiusY > 0 ? input.footprint.radiusY : radius,
      }
    : { radiusX: radius, radiusY: radius };
  const originIsValid =
    Number.isFinite(from.x) &&
    Number.isFinite(from.y) &&
    from.x >= region.bounds.x + footprint.radiusX &&
    from.x <= region.bounds.x + region.bounds.w - footprint.radiusX &&
    from.y >= region.bounds.y + footprint.radiusY &&
    from.y <= region.bounds.y + region.bounds.h - footprint.radiusY &&
    !footprintCollidesWithRegion(region, from, footprint);
  if (!originIsValid) throw new RangeError('Invalid movement origin');

  const desiredX = Number.isFinite(desired.x) ? desired.x : from.x;
  const desiredY = Number.isFinite(desired.y) ? desired.y : from.y;
  const target = {
    x: clamp(desiredX, region.bounds.x + footprint.radiusX, region.bounds.x + region.bounds.w - footprint.radiusX),
    y: clamp(desiredY, region.bounds.y + footprint.radiusY, region.bounds.y + region.bounds.h - footprint.radiusY),
  };
  const candidates: Point[] = [target, { x: target.x, y: from.y }, { x: from.x, y: target.y }];
  const position =
    candidates.find(
      (candidate) =>
        !footprintCollidesWithRegion(region, candidate, footprint) &&
        pathIsClear(region, from, candidate, footprint),
    ) ?? from;

  return {
    position,
    blockedX: position.x !== target.x,
    blockedY: position.y !== target.y,
    crossedEntrance: entranceAt(region, position),
  };
}
